import * as m from '@staiter/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  CheckCircle2,
  Combine,
  Factory,
  PackageMinus,
  PackagePlus,
  Plus,
  Trash2,
  Workflow,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { EntityRefCell, PageHeader, Table } from '~/components/data';
import { BatchPicker, CounterpartyPicker, ItemPicker, WarehousePicker } from '~/components/domain';
import { useToast } from '~/components/feedback';
import {
  Button,
  Checkbox,
  DateTimePicker,
  Input,
  Select,
  WizardStepper,
} from '~/components/primitives';
import { applyValidationErrors } from '~/lib/api/apply-validation-errors';
import {
  type StockEntryDraft,
  type StockEntryType,
  createStockEntryDraft,
  submitPreviewStockEntry,
  submitStockEntry,
} from '~/lib/api/stock-entries';
import { formatMoney, formatQty } from '~/lib/format';
import { useZodForm } from '~/lib/forms/useZodForm';
import { useUiStore } from '~/store/ui';
import shared from './_auth._shared.module.css';
import styles from './_auth.stock-entries.new.module.css';

export const Route = createFileRoute('/_auth/stock-entries/new')({
  staticData: { minMainWidth: 960 },
  component: NewStockEntry,
});

type WizardType = StockEntryType | 'material_transfer_for_manufacture';

// FE-74/75 Sprint N+ batch 2: Lucide icons + semantic grouping (external /
// internal / production). FE-72: raw enum codes hidden from visible UI.
// FE-77 Sprint Q+: disambiguation hints (Требуется WO+BOM / Не влияет на себестоимость).
type SeWizardGroup = 'external' | 'internal' | 'production';
const TYPE_CARDS: {
  type: WizardType;
  group: SeWizardGroup;
  title: () => string;
  description: () => string;
  Icon: typeof PackagePlus;
  hint?: () => string;
}[] = [
  {
    type: 'material_receipt',
    group: 'external',
    title: m.route_se_new_type_material_receipt_title,
    description: m.route_se_new_type_material_receipt_desc,
    Icon: PackagePlus,
  },
  {
    type: 'material_issue',
    group: 'external',
    title: m.route_se_new_type_material_issue_title,
    description: m.route_se_new_type_material_issue_desc,
    Icon: PackageMinus,
  },
  {
    type: 'material_transfer',
    group: 'internal',
    title: m.route_se_new_type_material_transfer_title,
    description: m.route_se_new_type_material_transfer_desc,
    Icon: ArrowRightLeft,
    // FE-77: disambiguate vs transfer-for-manufacture (no WO required).
    hint: m.se_type_hint_no_cost,
  },
  {
    type: 'repack',
    group: 'internal',
    title: m.route_se_new_type_repack_title,
    description: m.route_se_new_type_repack_desc,
    Icon: Combine,
    hint: m.se_type_hint_no_cost,
  },
  {
    type: 'manufacture',
    group: 'production',
    title: m.route_se_new_type_manufacture_title,
    description: m.route_se_new_type_manufacture_desc,
    Icon: Factory,
    hint: m.se_type_hint_requires_wo,
  },
  {
    type: 'material_transfer_for_manufacture',
    group: 'production',
    title: m.route_se_new_type_wip_transfer_title,
    description: m.route_se_new_type_wip_transfer_desc,
    Icon: Workflow,
    hint: m.se_type_hint_requires_wo,
  },
];

const SE_GROUP_LABEL: Record<SeWizardGroup, () => string> = {
  external: m.stock_entry_wizard_section_external,
  internal: m.stock_entry_wizard_section_internal,
  production: m.stock_entry_wizard_section_production,
};

const LineSchema = z.object({
  item_id: z.string().min(1, m.route_se_new_err_item_required()),
  source_warehouse_id: z.string().default(''),
  target_warehouse_id: z.string().default(''),
  qty: z
    .string()
    .min(1, m.route_se_new_err_qty_required())
    .regex(/^\d+(\.\d+)?$/, m.route_se_new_err_qty_format()),
  uom: z.string().min(1, m.route_se_new_err_uom_required()),
  /**
   * F1 [П-4] — цена строки. Поля не было вовсе, а контракт требовал ставку:
   * документ не мог быть создан ни при каком вводе. Обязательна она только у
   * прихода — там цена ЕСТЬ у оператора (она в накладной), и взять её из
   * истории нельзя: приход эту историю и создаёт. У остальных типов сервер
   * подставит последнюю оценку склада, и поле остаётся пустым по-честному.
   */
  basic_rate: z.string().default(''),
  /**
   * F1 [П-6] — партия ссылкой, а не строкой. Раньше здесь был свободный текст
   * `batch_no`, который контракт всё равно не принимал: сервер ждёт `batch_id`.
   * Ввод «партия 12» создавал ощущение записанного и не записывал ничего.
   */
  batch_id: z.string().default(''),
  serial_nos: z.string().default(''),
  is_serial_tracked: z.boolean().default(false),
});

const FormSchema = z.object({
  posting_at: z.string().min(1, m.route_se_new_err_date_required()),
  /** F1 — необязателен: пустой означает «сгенерировать серверу» (`SE-YYYY-NNNNN`). */
  voucher_no: z.string().default(''),
  supplier_id: z.string().default(''),
  cost_center: z.string().default(''),
  work_order_id: z.string().default(''),
  strict_expiry: z.boolean().default(true),
  lines: z.array(LineSchema).min(1, m.route_se_new_err_lines_min()),
  notes: z.string().default(''),
});
type FormValues = z.infer<typeof FormSchema>;

/** Тип, у которого цена обязательна на сервере. Держим рядом с формой — по нему
 *  и помечается поле, и ставится inline-ошибка шага. */
const RATE_REQUIRED_TYPES: ReadonlySet<WizardType> = new Set(['material_receipt']);

/**
 * F1 — список единиц ВСЕГДА содержит единицу выбранной позиции.
 *
 * Поймано живьём на гейте A: список был из пяти зашитых латинских кодов
 * (`pcs`/`kg`/`m`/`m2`/`l`), а карточка номенклатуры отдаёт то, что в ней
 * записано — на стенде это «шт», «м.п.», «пара», «пог. м», двенадцать разных
 * значений. Единица подставлялась в поле, в списке её не было, и контрол
 * показывал «—»: оператор видел ПУСТУЮ единицу у строки, где она на самом деле
 * есть, и в теле запроса уходило верное значение. Расхождение того же класса,
 * что и весь этот пак: экран говорит одно, данные другое.
 *
 * Зашитый список оставлен как подсказка для позиций без единицы в карточке; он
 * дополняется фактическим значением, а не подменяет его.
 */
function uomOptions(current: string): Array<{ value: string; label: string }> {
  const base = [
    { value: 'pcs', label: m.route_se_new_uom_pcs() },
    { value: 'kg', label: m.route_se_new_uom_kg() },
    { value: 'm', label: m.route_se_new_uom_m() },
    { value: 'm2', label: m.route_se_new_uom_m2() },
    { value: 'l', label: m.route_se_new_uom_l() },
  ];
  if (current && !base.some((o) => o.value === current)) {
    return [{ value: current, label: current }, ...base];
  }
  if (!current) return [{ value: '', label: m.route_se_new_uom_pending() }, ...base];
  return base;
}

function emptyLine(): z.infer<typeof LineSchema> {
  return {
    item_id: '',
    source_warehouse_id: '',
    target_warehouse_id: '',
    qty: '',
    // F55 (WHS-F16): до выбора позиции единица не известна — пустое поле, а не
    // правдоподобное «шт». Выбор позиции подставляет единицу из карточки.
    uom: '',
    basic_rate: '',
    batch_id: '',
    serial_nos: '',
    is_serial_tracked: false,
  };
}

function NewStockEntry(): JSX.Element {
  const nav = useNavigate();
  const toast = useToast();
  // Wizard-level state (step, type, draftId) — остаётся useState:
  // RHF управляет только полями формы, шаги и id черновика — state машина снаружи.
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [type, setType] = useState<WizardType | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  /**
   * F1 [П-6] — какие позиции ведутся партиями. Ключ — `item_id`, а не индекс
   * строки: одна позиция может стоять в двух строках, и признак у неё один.
   */
  const [batchTracked, setBatchTracked] = useState<Record<string, boolean>>({});

  // FE-105 Sprint Q+: autosave draft в localStorage (опционально восстановить).
  const AUTOSAVE_KEY = 'staiter.se_draft';
  const restoredDraft = useMemo(() => {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as Partial<FormValues>;
    } catch {
      return null;
    }
  }, []);
  const form = useZodForm(FormSchema, {
    posting_at: restoredDraft?.posting_at || new Date().toISOString().slice(0, 16),
    voucher_no: restoredDraft?.voucher_no || '',
    supplier_id: restoredDraft?.supplier_id || '',
    cost_center: restoredDraft?.cost_center || '',
    work_order_id: restoredDraft?.work_order_id || '',
    strict_expiry: restoredDraft?.strict_expiry ?? true,
    lines:
      restoredDraft?.lines && restoredDraft.lines.length > 0 ? restoredDraft.lines : [emptyLine()],
    notes: restoredDraft?.notes || '',
  });
  const { control, register, watch, getValues, trigger, formState } = form;
  const { errors } = formState;

  const linesArr = useFieldArray({ control, name: 'lines' });

  // FE-105: debounced autosave каждые 2s после изменений в form.
  useEffect(() => {
    const sub = watch((values) => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(values));
      } catch {
        /* ignore quota/private mode */
      }
    });
    return () => sub.unsubscribe();
  }, [watch]);
  // Clear draft on successful submit.
  useEffect(() => {
    if (draftId) {
      try {
        localStorage.removeItem(AUTOSAVE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [draftId]);

  const variant = useMemo(() => buildVariant(type), [type]);

  // Step-3 submit-preview after draft is created.
  const previewQuery = useQuery({
    queryKey: ['stock-entry-preview', draftId],
    queryFn: () => submitPreviewStockEntry(draftId!),
    enabled: !!draftId,
  });

  const createDraft = useMutation({
    mutationFn: (data: FormValues) => {
      /**
       * F1 [П-1][П-12] — тело собирается ПО shared-схеме, поле в поле.
       *
       * Прежняя сборка не совпадала со схемой ни одним именем и вдобавок
       * схлопывала передачу в производство в обычное перемещение
       * (`mapWizardToBackend`): операция теряла смысл по дороге. Теперь тип
       * уходит как есть — `material_transfer_for_manufacture` существует в
       * контракте, и его надо было не переводить, а передать.
       */
      /**
       * Склады ШАПКИ, а не только строк.
       *
       * Поймано живьём на гейте A: документ создавался и проводился, движения
       * писались верно — а в реестре его НЕ БЫЛО. Отбор по области складов
       * смотрит на `stock_entries.from_warehouse_id/to_warehouse_id`, и пустая
       * шапка выпадает из области КАЖДОГО кладовщика: свой же документ он не
       * найдёт. Заполняем, когда склад на все строки один — а когда их
       * несколько, шапка честно пуста, и это отдельная строка книги.
       */
      const oneOf = (pick: (l: (typeof data.lines)[number]) => string): string | null => {
        const vals = new Set(data.lines.map(pick).filter(Boolean));
        return vals.size === 1 ? [...vals][0]! : null;
      };
      const headerFrom = variant.needsSource ? oneOf((l) => l.source_warehouse_id) : null;
      const headerTo = variant.needsTarget ? oneOf((l) => l.target_warehouse_id) : null;

      const draft: StockEntryDraft = {
        stock_entry_type: type!,
        posting_datetime: new Date(data.posting_at).toISOString(),
        ...(headerFrom ? { from_warehouse_id: headerFrom } : {}),
        ...(headerTo ? { to_warehouse_id: headerTo } : {}),
        ...(data.voucher_no.trim() ? { voucher_no: data.voucher_no.trim() } : {}),
        ...(variant.needsSupplier && data.supplier_id ? { supplier_id: data.supplier_id } : {}),
        ...(variant.needsCostCenter && data.cost_center.trim()
          ? { cost_center: data.cost_center.trim() }
          : {}),
        ...(variant.needsWorkOrder && data.work_order_id
          ? { work_order_id: data.work_order_id }
          : {}),
        ...(data.notes ? { remarks: data.notes } : {}),
        items: data.lines.map((l, idx) => ({
          position: idx,
          item_id: l.item_id,
          ...(variant.needsSource ? { from_warehouse_id: l.source_warehouse_id || null } : {}),
          ...(variant.needsTarget ? { to_warehouse_id: l.target_warehouse_id || null } : {}),
          ...(l.batch_id ? { batch_id: l.batch_id } : {}),
          qty: l.qty,
          uom: l.uom,
          // Цена уходит только когда введена: пустая означает «пусть сервер
          // возьмёт последнюю оценку», а не «ноль».
          ...(l.basic_rate.trim()
            ? { basic_rate: { amount: l.basic_rate.trim(), currency: 'KZT' as const } }
            : {}),
        })),
      };
      return createStockEntryDraft(draft);
    },
    onSuccess: (res) => {
      // П-2: идентификатор лежит в шапке ответа, а не в его корне.
      setDraftId(res.header.id);
      setStep(3);
    },
    onError: (err: Error) => {
      // Серверные адреса полей — `items.N.basic_rate`; в форме они зовутся
      // `lines.N.…`. Подмена первого сегмента и делает ошибку видимой У ПОЛЯ.
      if (applyValidationErrors(form, err, { items: 'lines' })) return;
      toast.push({
        variant: 'error',
        title: m.route_se_new_toast_draft_failed(),
        description: err.message,
      });
    },
  });

  const submitMut = useMutation({
    mutationFn: () => submitStockEntry(draftId!),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_se_new_toast_submitted() });
      // Идентификатор берём из состояния мастера, а не из ответа: документ тот
      // же, и лишняя зависимость от формы ответа — лишний способ сломаться.
      nav({ to: '/stock-entries/$id', params: { id: draftId! } });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_se_new_toast_submit_failed(),
        description: err.message,
      }),
  });

  /**
   * F3 [WHS-F5] — переход на шаг 2 больше не отказывает молча.
   *
   * ЧТО БЫЛО. Четыре `return` без единого слова: не заполнен поставщик, место
   * затрат, заказ или склад — кнопка «Проверить и посмотреть предпросмотр» не
   * делала НИЧЕГО. Ни запроса, ни ошибки, ни подсветки. Поле при этом выглядело
   * необязательным. Молчащая кнопка читается как сломанная кнопка, а не как
   * незаполненная форма: человек жмёт ещё раз, потом уходит.
   *
   * ПОЧЕМУ НЕ ОДИН ТОСТ НА ВСЁ. Тост говорит «что-то не так» и исчезает; чинить
   * по нему нечего. Ошибка обязана стоять У ПОЛЯ — и стоит: `setError` по тому
   * же пути, каким поле зарегистрировано, плюс фокус на первое незаполненное.
   */
  const goToStep2 = async (): Promise<void> => {
    /**
     * Ошибки предыдущей попытки снимаются ПЕРЕД новой проверкой.
     *
     * Поймано живьём: `setError` ставит ошибку вручную, и RHF её сам не
     * снимает — заполненный поставщик продолжал висеть с надписью «Выберите
     * поставщика». Форма, которая ругается на заполненное поле, читается как
     * сломанная ровно так же, как молчащая кнопка.
     */
    form.clearErrors();
    const ok = await trigger(['posting_at', 'lines']);
    if (!type) return;
    const data = getValues();
    const missing: Array<{ path: string; message: string }> = [];

    if (variant.needsSupplier && !data.supplier_id.trim()) {
      missing.push({ path: 'supplier_id', message: m.route_se_new_err_supplier_required() });
    }
    if (variant.needsCostCenter && !data.cost_center.trim()) {
      missing.push({ path: 'cost_center', message: m.route_se_new_err_cost_center_required() });
    }
    if (variant.needsWorkOrder && !data.work_order_id.trim()) {
      missing.push({ path: 'work_order_id', message: m.route_se_new_err_wo_required() });
    }
    data.lines.forEach((l, i) => {
      if (variant.needsSource && !l.source_warehouse_id.trim()) {
        missing.push({
          path: `lines.${i}.source_warehouse_id`,
          message: m.route_se_new_err_source_required(),
        });
      }
      if (variant.needsTarget && !l.target_warehouse_id.trim()) {
        missing.push({
          path: `lines.${i}.target_warehouse_id`,
          message: m.route_se_new_err_target_required(),
        });
      }
      // Цена прихода: сервер всё равно отобьёт 422 — но узнать об этом ДО
      // запроса дешевле, и ошибка встаёт у того же поля, что и серверная.
      if (RATE_REQUIRED_TYPES.has(type) && !l.basic_rate.trim()) {
        missing.push({
          path: `lines.${i}.basic_rate`,
          message: m.route_se_new_err_rate_required(),
        });
      }
    });

    for (const miss of missing) {
      // @ts-expect-error путь динамический, RHF принимает строку
      form.setError(miss.path, { type: 'required', message: miss.message });
    }
    if (!ok || missing.length > 0) {
      const first = missing[0]?.path;
      // @ts-expect-error путь динамический
      if (first) form.setFocus(first);
      return;
    }
    createDraft.mutate(data);
  };

  const strictExpiry = watch('strict_expiry');
  /*
   * F55 (WHS-F14): мастер — задача на всю ширину (Designing for Intent), навигация
   * свёрнута на время мастера и возвращается при уходе.
   */
  const setSidebarCollapsedOverride = useUiStore((st) => st.setSidebarCollapsedOverride);
  useEffect(() => {
    setSidebarCollapsedOverride(true);
    return () => setSidebarCollapsedOverride(null);
  }, [setSidebarCollapsedOverride]);

  /**
   * Раунд 2 гейта A, UX-2 — сумма строки и итог документа.
   *
   * Считаются ТЕМ ЖЕ правилом, что и на сервере (`stock_qty × rate`), но здесь
   * это подсказка глазу, а не источник правды: в базу пишет сервер. Поэтому
   * Decimal тут не нужен, а расхождение в последнем знаке не имеет значения —
   * оператор сверяет порядок величины с накладной.
   */
  const watchedLines = watch('lines');
  const lineAmountOf = (i: number): string => {
    const l = watchedLines?.[i];
    if (!l?.qty || !l?.basic_rate) return '—';
    const qty = Number(l.qty);
    const rate = Number(l.basic_rate);
    if (!Number.isFinite(qty) || !Number.isFinite(rate)) return '—';
    return formatMoney({ amount: String(qty * rate), currency: 'KZT' });
  };
  const hasAnyAmount = (watchedLines ?? []).some(
    (l) => Number.isFinite(Number(l.qty)) && Number.isFinite(Number(l.basic_rate)) && l.basic_rate,
  );
  const linesTotal = {
    amount: String(
      (watchedLines ?? []).reduce((acc, l) => {
        const qty = Number(l.qty);
        const rate = Number(l.basic_rate);
        return Number.isFinite(qty) && Number.isFinite(rate) ? acc + qty * rate : acc;
      }, 0),
    ),
    currency: 'KZT',
  };

  const previewRows = previewQuery.data?.expected_sle ?? [];
  const violations = previewQuery.data?.negative_stock_violations ?? [];
  const blockingViolations = violations.filter((v) => !v.allow_negative);
  const previewWarnings = previewQuery.data?.warnings ?? [];
  const blockingWarnings = previewWarnings.filter((w) => w.blocking);
  const canSubmit =
    !!previewQuery.data &&
    blockingViolations.length === 0 &&
    blockingWarnings.length === 0 &&
    !submitMut.isPending;

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.route_se_new_title()}
        description={m.route_se_new_subtitle({
          step,
          type: type ? labelOf(type) : m.route_se_new_subtitle_type_placeholder(),
        })}
      />

      {/* FE-73 Sprint Q+: WizardStepper primitive вместо plain text «Шаг 1 из 3». */}
      <WizardStepper
        ariaLabel={m.se_wizard_stepper_aria()}
        current={step}
        steps={[
          { n: 1, label: m.se_wizard_step1_label() },
          { n: 2, label: m.se_wizard_step2_label() },
          { n: 3, label: m.se_wizard_step3_label() },
        ]}
      />

      {step === 1 && (
        <section>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <h3 className={shared.sectionHeading}>{m.route_se_new_step1_heading()}</h3>
            {/* FE-79 Sprint Q+: help link для disambiguation типа операции. */}
            <a
              href="https://docs.staiter.io/inventory/stock-entry-types"
              target="_blank"
              rel="noopener noreferrer"
              className={shared.link}
              style={{ fontSize: '12px' }}
            >
              {m.stock_entry_wizard_help_link()}
            </a>
          </div>
          {/* FE-75 Sprint N+ batch 2: semantic grouping External / Internal / Production. */}
          {(['external', 'internal', 'production'] as const).map((group) => {
            const groupCards = TYPE_CARDS.filter((c) => c.group === group);
            if (groupCards.length === 0) return null;
            return (
              <div key={group} className={styles.typeGroup}>
                <h4 className={styles.typeGroupHeading}>{SE_GROUP_LABEL[group]()}</h4>
                <div
                  className={styles.typeGrid}
                  role="radiogroup"
                  aria-label={m.route_se_new_typegroup_aria()}
                >
                  {groupCards.map((c) => {
                    const IconComp = c.Icon;
                    return (
                      <button
                        key={c.type}
                        type="button"
                        role="radio"
                        aria-checked={type === c.type}
                        onClick={() => {
                          setType(c.type);
                          setStep(2);
                        }}
                        className={[styles.typeCard, type === c.type ? styles.typeCardSelected : '']
                          .filter(Boolean)
                          .join(' ')}
                        data-testid={`se-type-${c.type}`}
                      >
                        {/* FE-74: Lucide icon вместо emoji. FE-72: raw enum codes
                            убраны из visible UI (только data-testid для QA).
                            FE-77 Sprint Q+: disambiguation hint chip. */}
                        <span className={styles.typeCardIcon} aria-hidden="true">
                          <IconComp size={24} />
                        </span>
                        <strong className={styles.typeCardTitle}>{c.title()}</strong>
                        <p className={styles.typeCardDesc}>{c.description()}</p>
                        {c.hint && <span className={styles.typeCardHint}>{c.hint()}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {step === 2 && type && (
        <section>
          <div className={styles.fieldRow}>
            <Controller
              control={control}
              name="posting_at"
              render={({ field, fieldState }) => (
                <DateTimePicker
                  label={m.route_se_new_posting_at_label()}
                  value={field.value || null}
                  onChange={(v) => field.onChange(v ?? '')}
                  error={fieldState.error?.message}
                />
              )}
            />
            {variant.needsSupplier && (
              <Controller
                control={control}
                name="supplier_id"
                render={({ field, fieldState }) => (
                  <CounterpartyPicker
                    label={m.route_se_new_supplier_label()}
                    hint={m.route_se_new_supplier_hint()}
                    kind="supplier"
                    required
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
            {variant.needsCostCenter && (
              <Input
                label={m.route_se_new_cost_center_label()}
                placeholder={m.route_se_new_cost_center_placeholder()}
                required
                error={errors.cost_center?.message}
                {...register('cost_center')}
              />
            )}
            {/* F1 — номер документа необязателен: пустой генерирует сервер.
                Раньше контракт требовал его, а спросить было негде. */}
            <Input
              label={m.route_se_new_voucher_label()}
              placeholder={m.route_se_new_voucher_placeholder()}
              hint={m.route_se_new_voucher_hint()}
              {...register('voucher_no')}
            />
            {variant.needsWorkOrder && (
              <Input
                label={m.route_se_new_wo_label()}
                required
                error={errors.work_order_id?.message}
                placeholder={m.route_se_new_wo_placeholder()}
                hint={m.route_se_new_wo_hint()}
                {...register('work_order_id')}
              />
            )}
          </div>

          {/* F55 (WHS-F14): политика срока годности — своим уровнем, а не рядом с
              полями идентификации документа (Level 0). */}
          <div className={styles.policyRow}>
            <h3 className={shared.sectionTitle}>{m.route_se_new_policy_title()}</h3>
            <Controller
              control={control}
              name="strict_expiry"
              render={({ field }) => (
                <Checkbox
                  label={m.route_se_new_strict_expiry_label()}
                  /* Раунд 3 гейта A, UX-4: «тумблер обязан пояснять последствие
                     включения прямо под собой». */
                  description={m.route_se_new_strict_expiry_hint()}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className={styles.strictToggle}
                />
              )}
            />
          </div>

          <h3 className={shared.sectionHeading}>{m.route_se_new_lines_heading()}</h3>
          <div className={styles.lineList}>
            {linesArr.fields.map((f, i) => (
              <div key={f.id} className={styles.fieldRow}>
                {/* F55 (WHS-F15): индекс строки — к какому ряду относится «удалить». */}
                <span className={styles.lineNo} aria-hidden="true">
                  {i + 1}
                </span>
                <Controller
                  control={control}
                  name={`lines.${i}.item_id` as const}
                  render={({ field, fieldState }) => (
                    <ItemPicker
                      className={styles.pickerField}
                      label={m.route_se_new_line_item_label()}
                      required
                      value={field.value}
                      onChange={(id) => {
                        field.onChange(id);
                        /**
                         * F1 [П-6] — единица приходит ИЗ КАРТОЧКИ номенклатуры,
                         * и вместе с ней — признак партионного учёта.
                         *
                         * Прежняя версия подставляла единицу только если в поле
                         * ещё стояло `'pcs'`, то есть смена позиции со штук на
                         * килограммы оставляла штуки. Здесь единица следует за
                         * позицией всегда: она свойство номенклатуры, а не ввод
                         * оператора.
                         */
                        if (id) {
                          void import('~/lib/api/inventory').then(({ fetchItem }) =>
                            fetchItem(id)
                              .then((item) => {
                                if (item?.default_uom) {
                                  form.setValue(`lines.${i}.uom`, item.default_uom);
                                }
                                setBatchTracked((prev) => ({
                                  ...prev,
                                  [id]: item?.has_batch_no === true,
                                }));
                                // Позиция сменилась — прежняя партия к ней не
                                // относится: партия принадлежит номенклатуре.
                                form.setValue(`lines.${i}.batch_id`, '');
                              })
                              .catch(() => {
                                /* сеть отвалилась — оставляем введённое */
                              }),
                          );
                        }
                      }}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                {variant.needsSource && (
                  <Controller
                    control={control}
                    name={`lines.${i}.source_warehouse_id` as const}
                    render={({ field, fieldState }) => (
                      <WarehousePicker
                        className={styles.pickerField}
                        label={m.route_se_new_line_source_label()}
                        required
                        value={field.value}
                        onChange={field.onChange}
                        excludeWarehouseId={watch(`lines.${i}.target_warehouse_id`) || undefined}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                )}
                {variant.needsTarget && (
                  <Controller
                    control={control}
                    name={`lines.${i}.target_warehouse_id` as const}
                    render={({ field, fieldState }) => (
                      <WarehousePicker
                        className={styles.pickerField}
                        label={m.route_se_new_line_target_label()}
                        required
                        value={field.value}
                        onChange={field.onChange}
                        excludeWarehouseId={watch(`lines.${i}.source_warehouse_id`) || undefined}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                )}
                <Input
                  label={m.route_se_new_line_qty_label()}
                  inputMode="decimal"
                  className={styles.qtyField}
                  required
                  {...register(`lines.${i}.qty` as const)}
                  error={errors.lines?.[i]?.qty?.message}
                />
                <Controller
                  control={control}
                  name={`lines.${i}.uom` as const}
                  render={({ field }) => (
                    <Select
                      label={m.route_se_new_line_uom_label()}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      options={uomOptions(field.value)}
                    />
                  )}
                />
                {/* F1 [П-4] — цена строки. Обязательна у прихода: там она есть
                    у оператора и её неоткуда взять серверу. У прочих типов
                    пустая означает «взять последнюю оценку склада». */}
                <Input
                  label={m.route_se_new_line_rate_label()}
                  inputMode="decimal"
                  className={styles.rateField}
                  required={type ? RATE_REQUIRED_TYPES.has(type) : false}
                  hint={
                    type && RATE_REQUIRED_TYPES.has(type)
                      ? m.route_se_new_line_rate_hint_required()
                      : m.route_se_new_line_rate_hint_optional()
                  }
                  error={errors.lines?.[i]?.basic_rate?.message}
                  {...register(`lines.${i}.basic_rate` as const)}
                />
                {/* F1 [П-6] — партия видна только у партионной номенклатуры:
                    поле, которое нечем заполнить, хуже отсутствующего. */}
                {batchTracked[watch(`lines.${i}.item_id`)] && (
                  <Controller
                    control={control}
                    name={`lines.${i}.batch_id` as const}
                    render={({ field, fieldState }) => (
                      <BatchPicker
                        label={m.route_se_new_line_batch_label()}
                        itemId={watch(`lines.${i}.item_id`)}
                        value={field.value}
                        onChange={field.onChange}
                        hint={
                          strictExpiry
                            ? m.route_se_new_line_batch_hint_strict()
                            : m.route_se_new_line_batch_hint_loose()
                        }
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                )}
                <Controller
                  control={control}
                  name={`lines.${i}.is_serial_tracked` as const}
                  render={({ field }) => (
                    <Checkbox
                      label={m.route_se_new_line_serial_label()}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      className={styles.serialToggle}
                    />
                  )}
                />
                {watch(`lines.${i}.is_serial_tracked`) && (
                  <Input
                    label={m.route_se_new_line_serials_label()}
                    className={styles.serialInput}
                    placeholder="SN1, SN2, …"
                    {...register(`lines.${i}.serial_nos` as const)}
                  />
                )}
                {/* Раунд 5: сумма — ПОСЛЕДНЯЯ ячейка потока. Она и задаёт правую
                    вертикаль, по которой стоит итог документа. */}
                <div className={styles.lineAmount}>
                  <span className={styles.lineAmountLabel}>
                    {m.route_se_new_line_amount_label()}
                  </span>
                  <span className={`${styles.lineAmountValue} ${shared.mono}`}>
                    {lineAmountOf(i)}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className={styles.lineRemove}
                  leadingIcon={<Trash2 size={14} />}
                  onClick={() => linesArr.remove(i)}
                  aria-label={m.route_se_new_aria_remove_line()}
                  disabled={linesArr.fields.length <= 1}
                />
              </div>
            ))}
            <div className={styles.lineActions}>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                leadingIcon={<Plus size={14} />}
                // Гейт F59 (C): фокус не прыгает в «Количество» новой строки до выбора
                // позиции — при уходе с поля он сразу зажигал «Укажите количество».
                onClick={() => linesArr.append(emptyLine(), { shouldFocus: false })}
              >
                {m.route_se_new_btn_add_line()}
              </Button>
              {/* FE-101 Sprint Q+: bulk paste — несколько строк сразу через
                  TSV/CSV (item_sku\tqty\tuom) из Excel. */}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  const raw = window.prompt(m.se_bulk_paste_prompt());
                  if (!raw) return;
                  const rows = raw
                    .split(/\r?\n/)
                    .map((r) => r.trim())
                    .filter(Boolean)
                    .map((r) => r.split(/\t|;|,/));
                  let added = 0;
                  for (const cols of rows) {
                    if (cols.length < 2) continue;
                    const itemRef = (cols[0] ?? '').trim();
                    const qty = (cols[1] ?? '').trim();
                    const uom = (cols[2] ?? 'pcs').trim();
                    if (!itemRef || !qty) continue;
                    // Bulk paste uses item_id placeholder (требует выбор далее);
                    // qty + uom заполняются сразу.
                    linesArr.append({
                      ...emptyLine(),
                      qty,
                      uom: uom || 'pcs',
                    });
                    added++;
                  }
                  if (added === 0) {
                    // FE-N Pass 78: native alert → toast (Calm UI rule).
                    toast.push({ variant: 'warning', title: m.se_bulk_paste_no_rows() });
                  }
                }}
              >
                {m.se_bulk_paste_label()}
              </Button>
            </div>
            {/*
              Раунды 2–3 гейта A, UX-2 — итог документа считается ЗДЕСЬ, где
              оператор сверяет его с накладной, а не двумя шагами позже. Стоит
              в ПОДВАЛЕ карточки строк, а не над действием добавления: итог —
              следствие набора строк, и читается после него.
              Пока считать нечего, итог молчит прочерком: «0,00 ₸» на пустом
              документе и «—» в строке описывали бы одну пустоту двумя разными
              способами — дословная находка ревьюера.
            */}
            <div className={styles.linesTotal}>
              <span>{m.route_se_new_lines_total_label()}</span>
              <strong className={shared.mono}>
                {hasAnyAmount ? formatMoney(linesTotal) : '—'}
              </strong>
            </div>
          </div>

          <div className={styles.documentNotes}>
            <Input label={m.route_se_new_notes_label()} {...register('notes')} />
          </div>

          <div className={`${styles.stepActions} ${shared.stickyFooter}`}>
            <Button
              type="button"
              variant="ghost"
              leadingIcon={<ArrowLeft size={14} />}
              onClick={() => setStep(1)}
            >
              {m.route_se_new_btn_back()}
            </Button>
            <Button
              type="button"
              variant="primary"
              trailingIcon={<ArrowRight size={14} />}
              onClick={() => void goToStep2()}
              disabled={createDraft.isPending}
              loading={createDraft.isPending}
            >
              {m.route_se_new_btn_preview()}
            </Button>
          </div>
        </section>
      )}

      {step === 3 && draftId && (
        <section>
          <h3 className={shared.sectionHeading}>{m.route_se_new_step3_heading()}</h3>

          {previewQuery.isLoading && (
            <p className={shared.subtle}>{m.route_se_new_preview_loading()}</p>
          )}

          {previewQuery.data && (
            <>
              <Table
                ariaLabel={m.route_se_new_preview_aria()}
                data={previewRows}
                rowKey={(_, i) => `pre-${i}`}
                emptyTitle={m.route_se_new_preview_empty()}
                columns={[
                  {
                    id: 'item',
                    header: m.route_se_new_col_item(),
                    // F36 (WHS-F36): позиция и склад словами — сервер отдаёт подписи.
                    accessor: (r) => (
                      <EntityRefCell name={r.item_name ?? null} code={r.item_sku ?? null} />
                    ),
                  },
                  {
                    id: 'wh',
                    header: m.route_se_new_col_wh(),
                    accessor: (r) => (
                      <EntityRefCell
                        name={r.warehouse_name ?? null}
                        code={r.warehouse_code ?? null}
                      />
                    ),
                  },
                  {
                    id: 'batch',
                    header: m.route_se_new_col_batch(),
                    accessor: (r) => r.batch_no ?? '—',
                    mono: true,
                  },
                  {
                    id: 'qty',
                    header: m.route_se_new_col_qty_delta(),
                    accessor: (r) => formatQty(r.qty_change),
                    mono: true,
                    align: 'right',
                  },
                  {
                    id: 'rate',
                    header: m.route_se_new_col_rate(),
                    accessor: (r) => formatMoney(r.valuation_rate),
                    mono: true,
                    align: 'right',
                  },
                  {
                    id: 'val',
                    header: m.route_se_new_col_value_delta(),
                    // F36: изменение стоимости — qty × ставка с сервера, а не заглушка остатка.
                    accessor: (r) => (r.value_change ? formatMoney(r.value_change) : '—'),
                    mono: true,
                    align: 'right',
                  },
                ]}
              />

              {violations.length > 0 && (
                <div role="alert" className={styles.alertDanger}>
                  <strong>
                    <AlertTriangle size={14} aria-hidden="true" />{' '}
                    {m.route_se_new_violations_heading()}
                  </strong>
                  <ul className={styles.alertList}>
                    {violations.map((v) => (
                      <li
                        key={`${v.item_id}-${v.warehouse_id}`}
                        className={v.allow_negative ? styles.violationWarn : styles.violationDanger}
                      >
                        {m.route_se_new_violation_row({
                          item: v.item_label ?? v.item_id.slice(0, 8),
                          warehouse: v.warehouse_label ?? v.warehouse_id.slice(0, 8),
                          current: String(v.current_qty),
                          required: String(v.required_qty),
                        })}
                        {!v.allow_negative && m.route_se_new_violation_blocked_suffix()}
                        {v.allow_negative && m.route_se_new_violation_allowed_suffix()}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {previewWarnings.length > 0 && (
                <div role="status" className={styles.alertWarn}>
                  <strong>{m.route_se_new_warnings_heading()}</strong>
                  <ul className={styles.alertList}>
                    {/* Текст предупреждения собирает ФРОНТ по коду типа: бэкенд
                        свободных строк в UI-схемах не шлёт (правило №19). */}
                    {previewWarnings.map((w) => (
                      <li key={`${w.type}-${w.related_item_id ?? w.related_batch_id ?? ''}`}>
                        {m.se_submit_warning_by_type(w.type)}
                        {w.blocking && <strong>{m.route_se_new_warning_block_suffix()}</strong>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <div className={`${styles.stepActions} ${shared.stickyFooter}`}>
            <Button
              type="button"
              variant="ghost"
              leadingIcon={<ArrowLeft size={14} />}
              onClick={() => setStep(2)}
            >
              {m.route_se_new_btn_back()}
            </Button>
            <Button
              type="button"
              variant="primary"
              leadingIcon={<CheckCircle2 size={14} />}
              onClick={() => submitMut.mutate()}
              disabled={!canSubmit}
              loading={submitMut.isPending}
            >
              {m.route_se_new_btn_submit()}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function labelOf(t: WizardType): string {
  const card = TYPE_CARDS.find((c) => c.type === t);
  return card ? card.title() : t;
}

function buildVariant(t: WizardType | null): {
  needsSource: boolean;
  needsTarget: boolean;
  needsSupplier: boolean;
  needsCostCenter: boolean;
  needsWorkOrder: boolean;
} {
  if (!t) {
    return {
      needsSource: false,
      needsTarget: false,
      needsSupplier: false,
      needsCostCenter: false,
      needsWorkOrder: false,
    };
  }
  switch (t) {
    case 'material_receipt':
      return {
        needsSource: false,
        needsTarget: true,
        needsSupplier: true,
        needsCostCenter: false,
        needsWorkOrder: false,
      };
    case 'material_issue':
      return {
        needsSource: true,
        needsTarget: false,
        needsSupplier: false,
        needsCostCenter: true,
        needsWorkOrder: false,
      };
    case 'material_transfer':
      return {
        needsSource: true,
        needsTarget: true,
        needsSupplier: false,
        needsCostCenter: false,
        needsWorkOrder: false,
      };
    case 'manufacture':
      return {
        needsSource: true,
        needsTarget: true,
        needsSupplier: false,
        needsCostCenter: false,
        needsWorkOrder: true,
      };
    case 'repack':
      return {
        needsSource: true,
        needsTarget: true,
        needsSupplier: false,
        needsCostCenter: false,
        needsWorkOrder: false,
      };
    case 'material_transfer_for_manufacture':
      return {
        needsSource: true,
        needsTarget: true,
        needsSupplier: false,
        needsCostCenter: false,
        needsWorkOrder: true,
      };
  }
}
